import { Texture, Vector3 } from "three";
import { AutoAttenuatableTextSprite } from "../object/AutoAttenuatableTextSprite";
import { findMultiplesOf2 } from "../utils/math-utils";
import { drawRoundRect } from "../utils/draw-utils";
import * as Config from "../config/ViewerConfig";

export class MarkupText {
  private _text: string = "";
  public get text() {
    return this._text;
  }
  public set text(value: string) {
    this._text = value;
    this.updateTextContext2d();
  }

  private _texture: Texture;
  private _sprite: AutoAttenuatableTextSprite;
  public get model() {
    return this._sprite;
  }

  public set position(pos: Vector3) {
    this._sprite.setPosition(pos);
  }
  public get position() {
    return this._sprite.getPosition();
  }

  public get textSize() {
    return this._sprite.textSize;
  }
  public set textSize(value: number) {
    this._sprite.textSize = value;
  }

  constructor(textScale: Config.ITextScale) {
    const textCanvas = document.createElement("canvas");
    this._texture = new Texture(textCanvas);
    this._sprite = new AutoAttenuatableTextSprite(this._texture, textScale);
    this._sprite.visible = false;
  }

  updateTextContext2d() {
    if (this._text.length === 0) {
      this._sprite.visible = false;
      this._texture.needsUpdate = true;
      return;
    }
    this._sprite.visible = true;

    const canvas = this._texture.image as HTMLCanvasElement;
    const context = canvas.getContext("2d") as CanvasRenderingContext2D;

    this._sprite.geometry.computeBoundingBox();
    const fontsize = 36;
    const padding = 5;
    const fontToUse = `${fontsize}px Arial`;

    // Calculate canvas size with the font and text
    context.font = fontToUse;
    const metrics = context.measureText(this._text);
    const textHeight =
      metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent;
    const textWidth = metrics.width;

    canvas.width = findMultiplesOf2(textWidth);
    canvas.height = findMultiplesOf2(textHeight);
    context.clearRect(0, 0, canvas.width, canvas.height);

    // Has to set font again after canvas size change.
    context.font = `${fontsize}px Arial`;

    // Draw background
    context.lineWidth = padding;
    context.fillStyle = "rgba(0,0,0,0.7)";
    context.strokeStyle = "rgba(0,0,0,0)";
    drawRoundRect(context, 0, 0, canvas.width, canvas.height, 6);

    // Draw text
    context.fillStyle = "rgba(255,255,255,1.0)";
    context.strokeStyle = "rgba(255,255,255,1.0)";
    context.fillText(this._text, padding, textHeight - padding);

    const drawWidth = Math.ceil(textWidth + 2 * padding);
    const drawHeight = Math.ceil(textHeight + 2 * padding);

    // update UVs
    const geom = this._sprite.geometry;
    const uvs = geom.attributes["uv"];
    const uvY = 1 - drawHeight / canvas.height;
    const uvX = drawWidth / canvas.width;
    uvs.setXY(0, 0, uvY);
    uvs.setXY(1, uvX, uvY);
    uvs.setXY(2, uvX, 1);
    uvs.setXY(3, 0, 1);
    uvs.needsUpdate = true;
    this._sprite.aspect = drawHeight / drawWidth;
    this._texture.needsUpdate = true;
  }
}
